import React, { useEffect, useRef, useState } from 'react';
import { BorderBeam } from 'border-beam';
import { useTranslation } from 'react-i18next';
import {
    Brush,
    CalendarCheck,
    Clapperboard,
    Check,
    ChevronLeft,
    Copy,
    Download,
    Expand,
    ImageUpscale,
    Loader2,
    MessageSquareText,
    Move3d,
    Pencil,
    RotateCw,
    SquareDashedMousePointer,
    Video,
    WandSparkles,
    X,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';

import { NodeShell, wasRecentlyDragged } from './NodeShell';
import { CircleActionButton, SvgRemove, SvgAgent } from './ProductNode';
import { RecipeOptionCard } from './RecipeOptionCard';
import type { EditTool } from './EditImageNode';
import { CsAsset } from '../../../../common/models/content-studio.model';

interface GenerationNodeProps {
    nodeId: string;
    x: number;
    y: number;
    asset: CsAsset;
    approved?: boolean;
    selected?: boolean;
    zoom: number;
    onDrag: (deltaX: number, deltaY: number) => void;
    onClick?: (modifiers: { ctrlKey: boolean; metaKey: boolean; shiftKey: boolean }) => void;
    onImageClick?: () => void;
    onApprove?: () => void;
    onReject?: () => void;
    onMakeVariation?: () => void;
    onMakeVideo?: () => void;
    /** Place a furniture product in this image. Only passed when the vendor has furniture. */
    onPlaceProduct?: () => void;
    /** Spawn an edit-image node, optionally pre-selecting a tool. */
    onEditImage?: (tool?: EditTool) => void;
    onDesign?: () => void;
    /** Put this picture or clip on the newest timeline (the video editor). */
    onAddToTimeline?: () => void;
    onDownload?: () => void;
    /** Take this finished picture to the scheduler, pre-selected. */
    onSchedulePost?: () => void;
    onRerun?: () => void;
    onDelete?: () => void;
    /** Point the chat at this node — the vendor supplies the words. */
    onTargetInChat?: () => void;
    /** Persist a free-form note on the asset. */
    onNoteChange?: (note: string) => void | Promise<void>;
    /** Faded / non-interactive rendering in workspace select mode. */
    dimmed?: boolean;
}

const NODE_WIDTH = 220;

// A pending generation is treated as "stuck" once it's been running longer than
// this — only then do we surface a Remove button (cancel doesn't refund credits,
// so it's purely an escape hatch for hung jobs, not a normal-flow action).
const STUCK_PENDING_MS = 30_000;

// Create-option icons (level-2, off an approved generation) — same neutral
// line weight as the level-1 recipe icons so both tiers match.
const OPT_ICON = 'size-5';

/**
 * Create-options menu shown off an approved generation's "+" handle. Two levels:
 * the root (Variation / Edit / Video) and the Edit sub-menu (Retouch / Expand /
 * Modify / Design). Edit + Design are unified under one "Edit" entry so the four
 * edit tools live together. Its own `view` state resets whenever the popover
 * closes (this component unmounts), so it always reopens at the root.
 */
const CreateOptionsMenu: React.FC<{
    isVideo: boolean;
    onMakeVariation?: () => void;
    onEditImage?: (tool?: EditTool) => void;
    onDesign?: () => void;
    onMakeVideo?: () => void;
    onPlaceProduct?: () => void;
    onAddToTimeline?: () => void;
    close: () => void;
}> = ({ isVideo, onMakeVariation, onEditImage, onDesign, onMakeVideo, onPlaceProduct, onAddToTimeline, close }) => {
    const { t } = useTranslation();
    const [view, setView] = useState<'root' | 'edit'>('root');
    const act = (fn?: () => void) => { fn?.(); close(); };
    const hasEdit = !!onEditImage && !isVideo;

    if (view === 'edit') {
        return (
            <div className="flex w-[252px] flex-col gap-1.5">
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setView('root'); }}
                    className="flex items-center gap-1.5 self-start px-1 py-0.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                >
                    <ChevronLeft className="size-4" />
                    {t('CONTENT_STUDIO.CREATE_BACK', 'Back')}
                </button>
                <RecipeOptionCard
                    index={0}
                    icon={<SquareDashedMousePointer className={OPT_ICON} />}
                    title={t('CONTENT_STUDIO.EDIT_TOOL_RETOUCH', 'Retouch')}
                    description={t('CONTENT_STUDIO.EDIT_TOOL_RETOUCH_HINT', 'Mask regions and edit by prompt')}
                    onClick={() => act(() => onEditImage?.('retouch'))}
                />
                <RecipeOptionCard
                    index={1}
                    icon={<Expand className={OPT_ICON} />}
                    title={t('CONTENT_STUDIO.EDIT_TOOL_EXPAND', 'Expand')}
                    description={t('CONTENT_STUDIO.EDIT_TOOL_EXPAND_HINT', 'Outpaint — extend the canvas on any side')}
                    onClick={() => act(() => onEditImage?.('expand'))}
                />
                <RecipeOptionCard
                    index={2}
                    icon={<WandSparkles className={OPT_ICON} />}
                    title={t('CONTENT_STUDIO.EDIT_TOOL_MODIFY', 'Modify by prompt')}
                    description={t('CONTENT_STUDIO.EDIT_TOOL_MODIFY_HINT', 'Add, remove, or swap elements via freeform prompt')}
                    onClick={() => act(() => onEditImage?.('modify'))}
                />
                <RecipeOptionCard
                    index={3}
                    icon={<ImageUpscale className={OPT_ICON} />}
                    title={t('CONTENT_STUDIO.EDIT_TOOL_UPSCALE', 'Upscale')}
                    description={t('CONTENT_STUDIO.EDIT_TOOL_UPSCALE_HINT', 'Boost resolution and detail')}
                    onClick={() => act(() => onEditImage?.('upscale'))}
                />
                <RecipeOptionCard
                    index={4}
                    icon={<Brush className={OPT_ICON} />}
                    title={t('CONTENT_STUDIO.NODE_DESIGN', 'Design')}
                    description={t('CONTENT_STUDIO.NODE_DESIGN_DESC', 'Add text, logos and graphics for a marketing-ready layout.')}
                    onClick={() => act(() => onEditImage?.('design'))}
                />
            </div>
        );
    }

    return (
        <div className="flex w-[252px] flex-col gap-1.5">
            {onMakeVariation && (
                <RecipeOptionCard
                    index={0}
                    icon={<Copy className={OPT_ICON} />}
                    title={t('CONTENT_STUDIO.GEN_ACTION_VARIATION')}
                    description={t('CONTENT_STUDIO.GEN_ACTION_VARIATION_DESC', 'Re-shoot the same subject with new lighting, framing or a close-up.')}
                    onClick={() => act(onMakeVariation)}
                />
            )}
            {hasEdit && (
                <RecipeOptionCard
                    index={1}
                    icon={<Pencil className={OPT_ICON} />}
                    title={t('CONTENT_STUDIO.CREATE_EDIT', 'Edit')}
                    description={t('CONTENT_STUDIO.CREATE_EDIT_DESC', 'Retouch, expand, modify or design this image.')}
                    onClick={() => setView('edit')}
                />
            )}
            {onMakeVideo && !isVideo && (
                <RecipeOptionCard
                    index={2}
                    icon={<Video className={OPT_ICON} />}
                    title={t('CONTENT_STUDIO.GEN_KIND_VIDEO')}
                    description={t('CONTENT_STUDIO.GEN_KIND_VIDEO_DESC', 'Animate this image into a short product video clip.')}
                    onClick={() => act(onMakeVideo)}
                />
            )}
            {onAddToTimeline && (
                <RecipeOptionCard
                    index={isVideo ? 0 : 4}
                    icon={<Clapperboard className={OPT_ICON} />}
                    title={t('VIDEO_EDITOR.ADD_TO_TIMELINE')}
                    description={t('VIDEO_EDITOR.ADD_TO_TIMELINE_DESC')}
                    onClick={() => act(onAddToTimeline)}
                />
            )}
            {onPlaceProduct && !isVideo && (
                <RecipeOptionCard
                    index={3}
                    icon={<Move3d className={OPT_ICON} />}
                    title={t('CONTENT_STUDIO.SEED.RECIPE.PLACE_IN_ROOM.NAME', 'Place in a room')}
                    description={t('CONTENT_STUDIO.PLACE.MENU_DESC', 'Put one of your furniture pieces in this room, then render it.')}
                    onClick={() => act(onPlaceProduct)}
                />
            )}
        </div>
    );
};

/**
 * Generation node — shows the result of a generation job. Three visual
 * states:
 *   pending  → spinner + pulsing border
 *   ready    → protected image thumbnail + hover affordances
 *   failed   → red tint + error message
 *
 * On hover, reveals "Variation" / "Video" pills on the right side.
 * Click on the image → emits onImageClick (opens lightbox).
 */
export const GenerationNode: React.FC<GenerationNodeProps> = ({
    nodeId,
    x,
    y,
    asset,
    selected,
    zoom,
    onDrag,
    onClick,
    onImageClick,
    approved,
    onApprove,
    onReject,
    onMakeVariation,
    onMakeVideo,
    onPlaceProduct,
    onEditImage,
    onDesign,
    onAddToTimeline,
    onDownload,
    onSchedulePost,
    onRerun,
    onDelete,
    onTargetInChat,
    onNoteChange,
    dimmed,
}) => {
    const { t } = useTranslation();
    const isPending = asset.status === 'pending';
    const isFailed = asset.status === 'failed';
    const isReady = asset.status === 'ready';
    const isVideo = asset.kind === 'video';

    // Comment popover — ready assets only. Draft mirrors asset.note so
    // reopening shows the saved value; typing doesn't persist until Save.
    // Saving with an empty string clears the note server-side.
    const noteAnchorRef = useRef<HTMLButtonElement | null>(null);
    const [noteOpen, setNoteOpen] = useState(false);
    const [noteDraft, setNoteDraft] = useState(asset.note || '');
    const [noteSaving, setNoteSaving] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    useEffect(() => {
        if (!noteOpen) setNoteDraft(asset.note || '');
    }, [asset.note, noteOpen]);
    // Only offer Remove on a pending node once it's been running long enough to
    // look stuck (a normal quick generation shows no button).
    const [pendingStuck, setPendingStuck] = useState(false);
    useEffect(() => {
        if (asset.status !== 'pending') { setPendingStuck(false); return; }
        const created = new Date(asset.createdAt).getTime();
        const check = () => setPendingStuck(Date.now() - created > STUCK_PENDING_MS);
        check();
        const id = setInterval(check, 5000);
        return () => clearInterval(id);
    }, [asset.status, asset.createdAt]);
    const hasNote = !!(asset.note && asset.note.trim());
    // Icon visibility: always on when a note exists (acts as its indicator),
    // otherwise only while the card is hovered or the popover is open so
    // the canvas stays clean.
    const showNoteIcon = hasNote || isHovered || noteOpen;

    // The node's status colour, read straight off the theme so it tracks
    // light / dark with everything else.
    const accent = isFailed
        ? 'var(--destructive)'
        : isPending
        ? 'var(--muted-foreground)'
        : approved
        ? 'var(--success)'
        : 'var(--warning)';

    // Execution time — use finishedAt (set by worker when status becomes
    // terminal) so the duration is stable even if the asset doc is later
    // mutated (e.g. repositioned on the canvas). Falls back to updatedAt
    // for legacy assets generated before finishedAt was added.
    const terminalAt = asset.finishedAt || asset.updatedAt;
    const durationLabel = (isReady || isFailed)
        ? formatDuration(asset.createdAt, terminalAt)
        : null;

    const dimLabel = isReady && asset.dimensions
        ? `${asset.dimensions.width}×${asset.dimensions.height}`
        : null;

    const durationTone: 'success' | 'failure' | 'neutral' =
        isReady ? 'success' : isFailed ? 'failure' : 'neutral';

    const saveNote = async () => {
        setNoteSaving(true);
        try { await onNoteChange?.(noteDraft); } finally {
            setNoteSaving(false);
            setNoteOpen(false);
        }
    };

    return (
        <NodeShell
            nodeId={nodeId}
            x={x}
            y={y}
            width={NODE_WIDTH}
            zoom={zoom}
            selected={selected}
            accent={accent}
            collapsible={false}
            dimmed={dimmed}
            label={
                isFailed ? t('CONTENT_STUDIO.GEN_STATUS_FAILED')
                : isPending ? t('CONTENT_STUDIO.GEN_STATUS_GENERATING')
                : !approved ? t('CONTENT_STUDIO.GEN_STATUS_REVIEW')
                : isVideo ? t('CONTENT_STUDIO.GEN_KIND_VIDEO') : t('CONTENT_STUDIO.GEN_KIND_IMAGE')
            }
            labelBadge={dimLabel || undefined}
            labelBadgeTone="neutral"
            labelBadgeSecondary={durationLabel || undefined}
            labelBadgeSecondaryTone={durationLabel ? durationTone : 'neutral'}
            onDrag={onDrag}
            onClick={onClick}
            pulse={isPending}
            // Only an approved image gets the "+" (its CREATE options). Failed /
            // pending cards have no "+"; their actions live in the bottom buttons.
            addHandle={isReady && approved}
            // Approved image: the "+" opens the CREATE options (Variation / Edit /
            // Design / Video); Download + Remove live in the bottom hover buttons.
            renderActions={
                (isReady && approved)
                    ? (close: () => void) => (
                        <CreateOptionsMenu
                            isVideo={isVideo}
                            onMakeVariation={onMakeVariation}
                            onEditImage={onEditImage}
                            onDesign={onDesign}
                            onMakeVideo={onMakeVideo}
                            onPlaceProduct={onPlaceProduct}
                            onAddToTimeline={onAddToTimeline}
                            close={close}
                        />
                    )
                    : undefined
            }
            // Bottom hover circular buttons — like the other nodes. Per state:
            //   approved → Download + Remove
            //   failed   → Re-run + Remove   (no "+")
            //   pending  → Remove, ONLY once it looks stuck (no "+")
            bottomAffordance={
                (isReady && approved) ? (
                    <div className="flex gap-1.5">
                        {onDownload && (
                            <CircleActionButton
                                title={t('CONTENT_STUDIO.ACTION_DOWNLOAD', 'Download')}
                                icon={<Download />}
                                onClick={onDownload}
                            />
                        )}
                        {onSchedulePost && (
                            <CircleActionButton
                                title={t('CONTENT_STUDIO.ACTION_SCHEDULE_POST', 'Schedule as post') as string}
                                icon={<CalendarCheck />}
                                onClick={onSchedulePost}
                            />
                        )}
                        {onTargetInChat && (
                            <CircleActionButton
                                title={t('CONTENT_STUDIO.ACTION_ASK_AGENT', 'Ask the agent about this') as string}
                                icon={<SvgAgent />}
                                onClick={onTargetInChat}
                            />
                        )}
                        {onDelete && (
                            <CircleActionButton title={t('CONTENT_STUDIO.ACTION_REMOVE')} icon={<SvgRemove />} onClick={onDelete} destructive />
                        )}
                    </div>
                ) : isFailed ? (
                    <div className="flex gap-1.5">
                        {onRerun && (
                            <CircleActionButton title={t('CONTENT_STUDIO.ACTION_RERUN')} icon={<RotateCw />} onClick={onRerun} />
                        )}
                        {onTargetInChat && (
                            <CircleActionButton
                                title={t('CONTENT_STUDIO.ACTION_ASK_AGENT', 'Ask the agent about this') as string}
                                icon={<SvgAgent />}
                                onClick={onTargetInChat}
                            />
                        )}
                        {onDelete && (
                            <CircleActionButton title={t('CONTENT_STUDIO.ACTION_REMOVE')} icon={<SvgRemove />} onClick={onDelete} destructive />
                        )}
                    </div>
                ) : isPending && pendingStuck && onDelete ? (
                    <div className="flex gap-1.5">
                        <CircleActionButton title={t('CONTENT_STUDIO.ACTION_REMOVE')} icon={<SvgRemove />} onClick={onDelete} destructive />
                    </div>
                ) : undefined
            }
        >
            <BorderBeam
                size="md"
                colorVariant="ocean"
                duration={1.5}
                strength={0.3}
                active={isPending}
            >
            {/* Image area */}
            <div
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                className={cn(
                    // Its own rounding (the shell's 12px, less its 1px border):
                    // an image composited on its own layer is not reliably
                    // clipped by a rounded ancestor.
                    'relative aspect-square overflow-hidden rounded-[11px]',
                    isFailed ? 'bg-destructive/5' : 'bg-muted',
                    isReady ? 'cursor-pointer' : 'cursor-default',
                )}
                onClick={(e) => {
                    if (isReady && onImageClick && !wasRecentlyDragged()) {
                        e.stopPropagation();
                        onImageClick();
                    }
                }}
            >
                {/* Pending spinner */}
                {isPending && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-muted-foreground">
                        <Loader2 className="size-6 animate-spin" />
                        <span className="text-xs font-medium">
                            {t('CONTENT_STUDIO.GENERATING_BTN')}
                        </span>
                    </div>
                )}

                {/* Ready video — autoplay + loop + muted so it replays silently in the card */}
                {isReady && isVideo && asset.url && (
                    <video
                        src={asset.url}
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="auto"
                        // Context-menu prevention happens on the overlay below
                        onContextMenu={(e: React.MouseEvent) => e.preventDefault()}
                        className="pointer-events-none absolute inset-0 size-full bg-black object-cover select-none"
                    />
                )}

                {/* Ready image — a real <img> of the full-size file, so the
                    browser's own right-click menu works: copy the image, copy
                    its address, open it in a tab. Not draggable, or the
                    browser's drag would fight the node's. */}
                {isReady && !isVideo && (asset.url || asset.thumbnailUrl) && (
                    <img
                        src={asset.url || asset.thumbnailUrl}
                        alt=""
                        draggable={false}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 size-full rounded-[11px] object-cover select-none"
                    />
                )}

                {/* Hover tint for images — pointer-transparent, so right-click
                    still lands on the <img> and its browser menu. */}
                {isReady && !isVideo && (
                    <div className={cn('pointer-events-none absolute inset-0 transition-colors', isHovered && 'bg-foreground/5')} />
                )}

                {/* Transparent overlay to prevent right-click save on videos */}
                {isReady && isVideo && (
                    <div
                        onContextMenu={(e) => e.preventDefault()}
                        onDragStart={(e) => e.preventDefault()}
                        // Subtle hover zoom hint
                        className="pointer-events-auto absolute inset-0 transition-colors hover:bg-foreground/5"
                    />
                )}

                {/* Comment icon — top-right corner of the media. Shows only
                    on ready assets. Green dot signals an existing note. */}
                {isReady && onNoteChange && (
                    <Popover open={noteOpen} onOpenChange={setNoteOpen}>
                        <PopoverTrigger asChild>
                            <button
                                type="button"
                                ref={noteAnchorRef}
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    // Radix skips its own toggle once the event is
                                    // defaulted — which is how a click that merely
                                    // ended a drag stays a non-click.
                                    if (wasRecentlyDragged()) e.preventDefault();
                                }}
                                onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
                                onContextMenu={(e: React.MouseEvent) => e.preventDefault()}
                                title={hasNote ? t('CONTENT_STUDIO.GEN_EDIT_COMMENT') : t('CONTENT_STUDIO.GEN_ADD_COMMENT')}
                                aria-label={hasNote ? t('CONTENT_STUDIO.GEN_EDIT_COMMENT') : t('CONTENT_STUDIO.GEN_ADD_COMMENT')}
                                className={cn(
                                    // Hidden when no note and not hovered/open — keeps the canvas
                                    // visually calm but stays always-on for cards that have a note
                                    // (so the green dot indicator is discoverable).
                                    'absolute end-2 top-2 z-[2] flex size-7 items-center justify-center rounded-full bg-background/90 text-foreground shadow-sm backdrop-blur-sm transition-opacity hover:bg-background',
                                    showNoteIcon ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
                                )}
                            >
                                <MessageSquareText className="size-4" />
                                {hasNote && (
                                    <span className="absolute top-px right-px size-[7px] rounded-full border border-background bg-success" />
                                )}
                            </button>
                        </PopoverTrigger>

                        {/* Comment popover — anchored to the icon button */}
                        <PopoverContent
                            align="end"
                            className="w-[280px] gap-0"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="mb-2 text-xs font-medium tracking-[0.04em] text-muted-foreground uppercase">
                                Comment
                            </div>
                            <Textarea
                                value={noteDraft}
                                onChange={(e) => setNoteDraft(e.target.value)}
                                onKeyDown={(e: React.KeyboardEvent) => {
                                    // Cmd/Ctrl+Enter saves; plain Enter inserts a newline.
                                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                        e.preventDefault();
                                        void saveNote();
                                    }
                                }}
                                placeholder={t('CONTENT_STUDIO.GEN_NOTE_PLACEHOLDER')}
                                rows={4}
                                autoFocus
                                className="resize-y"
                            />
                            <div className="mt-2 flex justify-end gap-2">
                                <Button variant="ghost" size="sm" onClick={() => setNoteOpen(false)}>
                                    {t('CONTENT_STUDIO.CANCEL')}
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={noteSaving}
                                    onClick={() => void saveNote()}
                                >
                                    {noteSaving ? t('CONTENT_STUDIO.GEN_NOTE_SAVING') : t('CONTENT_STUDIO.GEN_NOTE_SAVE')}
                                </Button>
                            </div>
                        </PopoverContent>
                    </Popover>
                )}


                {/* Failed state — show a friendly generic message only.
                    Raw provider errors (status codes, stack traces, JSON
                    blobs) are kept in a native tooltip via `title` so
                    developers can still inspect via hover, but vendors
                    never see technical noise. */}
                {isFailed && (
                    <div
                        title={asset.error || undefined}
                        className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 px-4 text-center"
                    >
                        <span className="text-base font-medium text-destructive">
                            {t('CONTENT_STUDIO.GENERATION_FAILED')}
                        </span>
                        <span className="max-w-full text-xs text-destructive/80">
                            {t('CONTENT_STUDIO.GENERATION_FAILED_HINT')}
                        </span>
                    </div>
                )}
            </div>

            {/* Review actions — always visible when ready but not approved */}
            {isReady && !approved && (
                <div className="flex gap-1 border-t px-2 py-2">
                    {onApprove && (
                        <ReviewActionButton
                            onClick={(e) => { e.stopPropagation(); onApprove(); }}
                            tone="success"
                            icon={<Check />}
                            title={t('CONTENT_STUDIO.GEN_ACTION_APPROVE')}
                        />
                    )}
                    {onReject && (
                        <ReviewActionButton
                            onClick={(e) => { e.stopPropagation(); onReject(); }}
                            tone="destructive"
                            icon={<X />}
                            title={t('CONTENT_STUDIO.GEN_ACTION_REJECT')}
                        />
                    )}
                    {onRerun && (
                        <ReviewActionButton
                            onClick={(e) => { e.stopPropagation(); onRerun(); }}
                            tone="neutral"
                            icon={<RotateCw />}
                            label={t('CONTENT_STUDIO.ACTION_RERUN')}
                        />
                    )}
                </div>
            )}

            </BorderBeam>
        </NodeShell>
    );
};

GenerationNode.displayName = 'GenerationNode';

export const GENERATION_NODE_WIDTH = NODE_WIDTH;

/**
 * Human-readable duration between two ISO timestamps. Examples:
 *   "<1s", "12s", "1m 8s", "2h 4m". Returns null if either timestamp is
 *   missing or the range is non-positive.
 */
function formatDuration(startIso?: string, endIso?: string): string | null {
    if (!startIso || !endIso) return null;
    const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
    if (!Number.isFinite(ms) || ms <= 0) return null;
    const totalSec = Math.round(ms / 1000);
    if (totalSec < 1) return '<1s';
    if (totalSec < 60) return `${totalSec}s`;
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    if (min < 60) return sec === 0 ? `${min}m` : `${min}m ${sec}s`;
    const hr = Math.floor(min / 60);
    const rMin = min % 60;
    return rMin === 0 ? `${hr}h` : `${hr}h ${rMin}m`;
}

// ─── Review-action button ──────────────────────────────────────────────
//
// Approve / Reject / Re-run share a compact layout: icon-left + optional
// label. Extracted because the review row renders it three times with
// nothing differing but the tone and the glyph.

interface ReviewActionButtonProps {
    onClick: (e: React.MouseEvent) => void;
    tone: 'success' | 'destructive' | 'neutral';
    icon: React.ReactNode;
    /** When set, button shows icon + label side-by-side and flex-grows
     *  to fill available space. */
    label?: string;
    /** When label is omitted, this serves as the native tooltip /
     *  aria-label for the icon-only compact square button. */
    title?: string;
}

const REVIEW_TONE: Record<ReviewActionButtonProps['tone'], string> = {
    success: 'border-success/40 text-success hover:bg-success/5 hover:text-success',
    destructive: 'border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive',
    neutral: 'text-muted-foreground hover:text-foreground',
};

const ReviewActionButton: React.FC<ReviewActionButtonProps> = ({
    onClick,
    tone,
    icon,
    label,
    title,
}) => (
    <Button
        variant="outline"
        onClick={onClick}
        title={title}
        aria-label={title || label}
        className={cn(
            'h-auto gap-1.5 py-1.5 text-xs [&_svg]:size-[13px]',
            label ? 'flex-1 px-2' : 'w-[34px] shrink-0 px-0',
            REVIEW_TONE[tone],
        )}
    >
        {icon}
        {label}
    </Button>
);
